import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { getFoscamPresets, postPresetGoto, postPresetSave } from "@client/api/foscam";
import { t } from "@client/lib/dictionary";
import { cn } from "@client/lib/cn";
import { foscamErrMsg } from "@client/lib/foscam/apiError";
import { useFoscamStore } from "@client/store/foscam";
import { Button } from "@client/components/ui/button";
import { Input } from "@client/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@client/components/ui/dialog";

/**
 * Uložené pozice kamery: přejezd (kdokoli přihlášený) + uložení aktuální pozice (admin).
 *
 * MAZÁNÍ pozic tady schválně NENÍ — to je správa, která patří do Nastavení → Kamery.
 * Uložení naopak zůstává u kamery, protože vyžaduje živý obraz: nejdřív se kamera
 * někam namíří, pak se pozice pojmenuje.
 */

interface Props {
  slug: string;
  isAdmin: boolean;
  onPoke?: () => void;
  /** Otevřený dialog nesmí nechat ovládání ztlumit auto-hidem. */
  onDialogChange?: (open: boolean) => void;
}

export const PresetBar: React.FC<Props> = ({ slug, isAdmin, onPoke, onDialogChange }) => {
  const presets = useFoscamStore((s) => s.presets[slug]);
  const setPresets = useFoscamStore((s) => s.setPresets);
  const [busy, setBusy] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");

  // Presety načteme líně při prvním zobrazení ovládání.
  useEffect(() => {
    if (presets !== undefined) return;
    getFoscamPresets(slug)
      .then((r) => setPresets(slug, r.responseObject?.presets ?? []))
      .catch(() => setPresets(slug, []));
  }, [presets, slug, setPresets]);

  const openSave = (open: boolean) => {
    setSaveOpen(open);
    onDialogChange?.(open);
  };

  const gotoPreset = useCallback(
    async (name: string) => {
      setBusy(true);
      try {
        await postPresetGoto(slug, name);
      } catch (err) {
        toast.error(foscamErrMsg(err));
      } finally {
        setBusy(false);
      }
    },
    [slug]
  );

  const savePreset = async () => {
    const name = saveName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await postPresetSave(slug, name);
      toast.success(t.foscam.saved);
      openSave(false);
      setSaveName("");
      const r = await getFoscamPresets(slug).catch(() => null);
      if (r) setPresets(slug, r.responseObject?.presets ?? []);
    } catch (err) {
      toast.error(foscamErrMsg(err));
    } finally {
      setBusy(false);
    }
  };

  // 32 px výška je kompromis: pozic bývá víc vedle sebe, takže plných 44 px by
  // na telefonu zabralo celou šířku — ale je to pořád trefitelné prstem.
  const chip = cn(
    "h-8 rounded-md px-3 text-xs font-medium text-white",
    "bg-black/55 hover:bg-black/75 active:bg-black/85 backdrop-blur-sm",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    "disabled:opacity-50 disabled:pointer-events-none"
  );

  const list = presets ?? [];
  if (list.length === 0 && !isAdmin) return null;

  return (
    <div className="flex flex-wrap items-center gap-1" onPointerDown={onPoke}>
      {list.map((p) => (
        <button key={p} type="button" className={chip} disabled={busy} onClick={() => void gotoPreset(p)}>
          {p}
        </button>
      ))}

      {isAdmin && (
        <button
          type="button"
          className={cn(chip, "inline-flex items-center gap-1")}
          disabled={busy}
          title={t.foscam.presetSave}
          aria-label={t.foscam.presetSave}
          onClick={() => openSave(true)}
        >
          <Plus className="h-3.5 w-3.5" />
          {t.foscam.presetSave}
        </button>
      )}

      <Dialog open={saveOpen} onOpenChange={openSave}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.foscam.presetSaveTitle}</DialogTitle>
          </DialogHeader>
          <Input
            autoFocus
            placeholder={t.foscam.presetName}
            value={saveName}
            maxLength={32}
            onChange={(e) => setSaveName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void savePreset()}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => openSave(false)}>
              {t.app.cancel}
            </Button>
            <Button type="button" disabled={busy || !saveName.trim()} onClick={() => void savePreset()}>
              {t.app.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PresetBar;
